import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { ProjectAssigneesWithUserInfoDto } from '../../commonTypes/project.types';

export interface AssigneesState {
    projectAssignees: Record<string, ProjectAssigneesWithUserInfoDto[]>;
}

const initialState: AssigneesState = {
    projectAssignees: {},
};

const mergeDefinedAssignee = (
    current: ProjectAssigneesWithUserInfoDto | undefined,
    incoming: ProjectAssigneesWithUserInfoDto
): ProjectAssigneesWithUserInfoDto => {
    if (!current) return incoming;

    const merged: ProjectAssigneesWithUserInfoDto = { ...current };
    for (const [key, value] of Object.entries(incoming)) {
        if (value !== undefined) {
            (merged as Record<string, unknown>)[key] = value;
        }
    }

    return merged;
};

const projectAssigneesSlice = createSlice({
    name: 'projectAssignees',
    initialState,
    reducers: {
        setProjectAssignees: (
            state,
            { payload: { projectId, assignees } }: { payload: { projectId: string; assignees: ProjectAssigneesWithUserInfoDto[] } }
        ) => {
            const currentAssignees: ProjectAssigneesWithUserInfoDto[] = state.projectAssignees[projectId] || [];
            const currentByUserSub = new Map(currentAssignees.map((assignee: ProjectAssigneesWithUserInfoDto) => [assignee.userSub, assignee]));
            state.projectAssignees[projectId] = assignees.map((assignee: ProjectAssigneesWithUserInfoDto) =>
                mergeDefinedAssignee(currentByUserSub.get(assignee.userSub), assignee)
            );
        },
        invalidateProjectAssignees: (state, { payload: projectId }) => {
            delete state.projectAssignees[projectId];
        },
        addProjectAssignees: (state, { payload: { projectId, newAssignees } }) => {
            if (newAssignees.length) {
                state.projectAssignees[projectId] = state.projectAssignees[projectId]
                    ? [...state.projectAssignees[projectId], ...newAssignees]
                    : newAssignees;
            }
        },
        updateAssignee: (state, { payload }) => {
            const { projectId, userSub, updates } = payload;
            const projectAssignees = state.projectAssignees[projectId];
            if (projectAssignees) {
                state.projectAssignees[projectId] = projectAssignees.map(assignee =>
                    assignee.userSub === userSub
                        ? {
                              ...assignee,
                              ...(updates ?? {}),
                          }
                        : assignee
                );
            }
        },
    },
});

export const { setProjectAssignees, invalidateProjectAssignees, addProjectAssignees, updateAssignee } = projectAssigneesSlice.actions;

export const selectProjectAssignees = (state: RootState, projectId: string) => state.projectAssignees.projectAssignees[projectId] || [];

export default projectAssigneesSlice.reducer;
